import { add, startOfMinute, sub } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { mileString } from '@/constants/formats';
import { effectiveQty } from '@/components/ShipmentForm/utilities/serviceLines';
import { cloneDeep } from 'lodash';
import { DEFAULT_DELIVERY_TIMEFRAME, DEFAULT_SERVICE_TIME } from '@/constants/deliveryTimeframes';

const DEFAULT_SETTINGS = {
    preferences_service_time: DEFAULT_SERVICE_TIME,
    preferences_delivery_timeframe: DEFAULT_DELIVERY_TIMEFRAME,
};

const convertServiceTimeToHourMinutes = (serviceTime) => {
    const totalMinutes = serviceTime / 60000;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return { hours, minutes };
};

const fakeLunchDirections = {
    distance: {
        value: 0,
    },
    duration: {
        value: 0,
    },
};

const calcLoadTime = (items) => {
    if (items >= 20) {
        return { hours: 1, minutes: 0 };
    } else if (items >= 15) {
        return { hours: 0, minutes: 45 };
    } else if (items >= 10) {
        return { hours: 0, minutes: 30 };
    } else {
        return { hours: 0, minutes: 15 };
    }
};

const isServiceType = (ordersByKey, orders = [], type) => {
    return orders.some((key) => {
        const order = ordersByKey[key];
        // account for weird edge case where order is undefined
        if (!order) {
            return 'dock';
        }
        const serviceType = order.order_type === 'return' ? order.pickup_location_type : order.dropoff_location_type;
        return serviceType === type;
    });
};

export const roundTimeframe = (timestamp, buffer, interval = 30) => {
    const newTimestamp = new Date(timestamp);
    const down = Math.floor(timestamp.getMinutes() / interval) * interval;
    const rem = timestamp.getMinutes() % interval;
    const up = !buffer && rem * 2 > interval ? interval : 0;

    newTimestamp.setMinutes(down + up);
    newTimestamp.setSeconds(0);
    newTimestamp.setMilliseconds(0);
    return newTimestamp;
};

// Returns true when a delivery window's end falls past the ceiling, evaluated in the customer's local
// timezone. The ceiling ({ hours, minutes }) caps the latest a window may end; e.g. a 5:00 PM ceiling with
// a 4-hour arrival time frame makes the last window 1:00-5:00 PM. This never mutates the window — over-cap
// stops are left honest and flagged for a warning instead of being clamped into a window a driver can't hit.
export const windowEndExceedsCeiling = (delWindowEnd, dropoffZip, ceiling) => {
    if (!ceiling || (!ceiling.hours && !ceiling.minutes) || !delWindowEnd) return false;

    const end = new Date(delWindowEnd);
    const tz = zipcode_to_timezone.lookup(dropoffZip) || 'America/New_York';
    const tzOffset = getTimezoneOffset(tz, end);

    const localEnd = new Date(end.getTime() + tzOffset);
    const ceilingLocal = new Date(localEnd);
    ceilingLocal.setUTCHours(ceiling.hours, ceiling.minutes, 0, 0);
    const ceilingUTC = new Date(ceilingLocal.getTime() - tzOffset);

    return end > ceilingUTC;
};

// dispatch_settings is keyed by the partner_client_id (the order's shipper), with 'DEFAULT' holding the current user's own settings.
// Falls back to DEFAULT_SETTINGS for fields with a known hardcoded default, so callers never receive a null/undefined for those.
export const getDispatchSettingPreference = (setting, dispatch_settings, shipperId) => {
    return (
        dispatch_settings?.[shipperId]?.[setting] ?? dispatch_settings?.DEFAULT?.[setting] ?? DEFAULT_SETTINGS[setting]
    );
};

// True when a customer delivery stop's window ends past the ceiling configured for its shipper. Warehouse
// pickups, lunches, and the route start/end stops don't carry a customer-facing delivery window, so they
// never trip the ceiling. Derived from the persisted del_window_end so the warning stays in sync with the
// current setting without persisting any per-stop flag.
export const stopExceedsCeiling = (stop, ordersByKey, dispatch_settings) => {
    if (!stop?.del_window_end || stop.start || stop.end || stop.type === 'LUNCH') return false;
    if (stop.type === 'PICKUP' && !stop?.returns?.length) return false;

    const firstOrder = ordersByKey?.[stop.orders?.[0]];
    if (!firstOrder) return false;

    const ceiling = getDispatchSettingPreference(
        'preferences_delivery_window_ceiling',
        dispatch_settings,
        firstOrder.shipper_id
    );
    return windowEndExceedsCeiling(stop.del_window_end, firstOrder.dropoff_zip, ceiling);
};

// Per-stop service time in MINUTES: each order contributes item handling
// (Σ item.service_time × qty) plus service-line handling (Σ service.service_time × qty).
// The order-level portion (order.service_time — e.g. a service-callback/GE appointment
// duration) is only added as a FALLBACK when the order has no item/service-line handling,
// so an explicit line time isn't double-counted on top of it.
export const sumStopServiceMinutes = (orders, ordersByKey) =>
    (orders || []).reduce((total, id) => {
        const order = ordersByKey?.[id];
        if (!order) return total;
        const itemHandling = (order.items || []).reduce(
            (acc, item) => acc + (item.service_time ? item.service_time * (item.quantity || 0) : 0),
            0
        );
        // effectiveQty (0/missing -> 1) so a line's dwell matches the units pricing bills.
        const serviceHandling = (order.services || []).reduce(
            (acc, svc) => acc + (svc.service_time ? svc.service_time * effectiveQty(svc) : 0),
            0
        );
        const lineHandling = itemHandling + serviceHandling;
        // Order-level portion is a fallback: ignore it once any line-level handling exists.
        const orderPortion = lineHandling > 0 ? 0 : order.service_time || 0;
        return total + lineHandling + orderPortion;
    }, 0);

export const calcDefaultServiceTime = (stop, ordersByKey, dispatch_settings) => {
    if (stop.start) {
        return { hours: 0, minutes: 0 };
    }

    const orders = stop.orders || [];
    // Warehouse pickup, calc based on # items being loaded
    if (stop.type === 'PICKUP' && !stop?.returns?.length) {
        // Only add up delivery items here, no return items will be loaded at this stop
        const itemCount = orders.reduce((acc, id) => {
            const itemsForThisOrder = (ordersByKey[id]?.items || []).reduce((acc, item) => {
                return acc + (item.quantity || 0);
            }, 0);
            return acc + (itemsForThisOrder || 0);
        }, 0);

        return calcLoadTime(itemCount);
    }

    const resolvedServiceMinutes = sumStopServiceMinutes(orders, ordersByKey);

    // Deliveries and customer return pickups, 30min
    if (stop.type === 'DROPOFF' || (stop.type === 'PICKUP' && stop?.returns?.length)) {
        let _minutes;

        const firstOrder = ordersByKey?.[orders?.[0]];
        const shipperId = firstOrder?.shipper_id;
        const preferences_use_service_default_as_minimum = getDispatchSettingPreference(
            'preferences_use_service_default_as_minimum',
            dispatch_settings,
            shipperId
        );
        const preferences_service_time_white_glove = getDispatchSettingPreference(
            'preferences_service_time_white_glove',
            dispatch_settings,
            shipperId
        );
        const preferences_service_time = getDispatchSettingPreference(
            'preferences_service_time',
            dispatch_settings,
            shipperId
        );
        const preferences_service_time_first_dry_space = getDispatchSettingPreference(
            'preferences_service_time_first_dry_space',
            dispatch_settings,
            shipperId
        );
        const preferences_service_time_room_of_choice = getDispatchSettingPreference(
            'preferences_service_time_room_of_choice',
            dispatch_settings,
            shipperId
        );
        const preferences_service_time_curbside = getDispatchSettingPreference(
            'preferences_service_time_curbside',
            dispatch_settings,
            shipperId
        );

        if (isServiceType(ordersByKey, orders, 'whiteGlove')) {
            const serviceDefault = preferences_service_time_white_glove || preferences_service_time;

            if (preferences_use_service_default_as_minimum) {
                _minutes = resolvedServiceMinutes > serviceDefault ? resolvedServiceMinutes : serviceDefault;
            } else {
                _minutes = resolvedServiceMinutes || serviceDefault;
            }
            return {
                hours: 0,
                minutes: _minutes,
            };
        }

        if (isServiceType(ordersByKey, orders, 'roomOfChoice')) {
            const serviceDefault = preferences_service_time_room_of_choice || preferences_service_time;

            if (preferences_use_service_default_as_minimum) {
                _minutes = resolvedServiceMinutes > serviceDefault ? resolvedServiceMinutes : serviceDefault;
            } else {
                _minutes = resolvedServiceMinutes || serviceDefault;
            }
            return {
                hours: 0,
                minutes: _minutes,
            };
        }
        if (isServiceType(ordersByKey, orders, 'firstDrySpace')) {
            const serviceDefault = preferences_service_time_first_dry_space || preferences_service_time;

            if (preferences_use_service_default_as_minimum) {
                _minutes = resolvedServiceMinutes > serviceDefault ? resolvedServiceMinutes : serviceDefault;
            } else {
                _minutes = resolvedServiceMinutes || serviceDefault;
            }
            return {
                hours: 0,
                minutes: _minutes,
            };
        }
        if (isServiceType(ordersByKey, orders, 'curbside')) {
            const serviceDefault = preferences_service_time_curbside || preferences_service_time;

            if (preferences_use_service_default_as_minimum) {
                _minutes = resolvedServiceMinutes > serviceDefault ? resolvedServiceMinutes : serviceDefault;
            } else {
                _minutes = resolvedServiceMinutes || serviceDefault;
            }
            return {
                hours: 0,
                minutes: _minutes,
            };
        }

        if (preferences_use_service_default_as_minimum) {
            _minutes =
                resolvedServiceMinutes > preferences_service_time ? resolvedServiceMinutes : preferences_service_time;
        } else {
            _minutes = resolvedServiceMinutes || preferences_service_time;
        }

        return { hours: 0, minutes: _minutes };
    }
    return { hours: 0, minutes: 0 };
};

export const calculateTimeframes = (
    stopSequence,
    directions,
    routeStartTime,
    ordersByKey,
    stopMatch,
    getDriveDistance,
    getDriveTime,
    updateIndex = false,
    dispatch_settings
) => {
    let clonedDirections = cloneDeep(directions);

    const lunchStop = stopSequence.find((s) => s.type === 'LUNCH');
    if (lunchStop) {
        clonedDirections.splice(lunchStop.ordering, 0, fakeLunchDirections);
    }

    let totalService = { hours: 0, minutes: 0 };
    let totalDrivingTime = 0; // seconds
    let routeStatDrivingTime = 0;
    let totalDrivingDistance = 0; // meters

    const newStops = cloneDeep(stopSequence);

    clonedDirections.forEach((seq, idx) => {
        // Find the matching original stop
        const stop = newStops.find(stopMatch(seq, idx));

        const firstOrder = ordersByKey?.[stop.orders?.[0]];
        const shipperId = firstOrder?.shipper_id;
        const preferences_timeframe_buffer_apply = getDispatchSettingPreference(
            'preferences_timeframe_buffer_apply',
            dispatch_settings,
            shipperId
        );
        const preferences_timeframe_round = getDispatchSettingPreference(
            'preferences_timeframe_round',
            dispatch_settings,
            shipperId
        );
        const preferences_timeframe_round_minute = getDispatchSettingPreference(
            'preferences_timeframe_round_minute',
            dispatch_settings,
            shipperId
        );
        const preferences_timeframe_buffer = getDispatchSettingPreference(
            'preferences_timeframe_buffer',
            dispatch_settings,
            shipperId
        );
        const preferences_delivery_timeframe = getDispatchSettingPreference(
            'preferences_delivery_timeframe',
            dispatch_settings,
            shipperId
        );
        const preferences_delivery_window_floor = getDispatchSettingPreference(
            'preferences_delivery_window_floor',
            dispatch_settings,
            shipperId
        );

        // Update stop index
        if (updateIndex) {
            stop.ordering = idx;
        }

        // Get driving time and distance from the previous stop to this stop
        const drivingTime = getDriveTime(seq, totalDrivingTime);
        const drivingDistance = getDriveDistance(seq);

        // Only omit from total route stat, still need to add up for correct arrival times on final returns
        totalDrivingTime += drivingTime;
        if (!seq.omit) routeStatDrivingTime += drivingTime;
        if (!seq.omit) totalDrivingDistance += drivingDistance;

        // Calculate this stops service time
        let serviceTime;
        if (stop.service_time === 0 || stop.service_time) {
            serviceTime = convertServiceTimeToHourMinutes(stop.service_time);
        } else {
            serviceTime = calcDefaultServiceTime(stop, ordersByKey, dispatch_settings);
            stop.service_time = (serviceTime.hours * 60 + serviceTime.minutes) * 60000;
        }

        // Stop start time = routeStartTime + totalDriving time + totalService time up until this stop
        const stopStartTime = add(add(routeStartTime, { seconds: totalDrivingTime }), totalService);

        // Stop end time is stop start time + service time
        const stopEndTime = add(stopStartTime, serviceTime);

        // Calculated start/end times
        stop.stop_start_time = stopStartTime;
        stop.stop_end_time = stopEndTime;

        // Apply buffering and rounding to customer timeframes. Unrounded, the window still starts on a whole
        // minute: drive time carries seconds, and an integration holding the window to the minute
        // (Xcelerator Axis) reads seconds back as a change.
        let timeframeStart = startOfMinute(stopStartTime);

        const buffer = preferences_timeframe_buffer_apply && preferences_timeframe_buffer;
        if (buffer && !stop.end) {
            timeframeStart = sub(timeframeStart, { hours: 0, minutes: buffer });
            stop.del_window_buffer = buffer * 60 * 1000;
        }

        if (preferences_timeframe_round && !stop.end) {
            timeframeStart = roundTimeframe(timeframeStart, buffer, preferences_timeframe_round_minute || 30);
            stop.del_window_rounded = true;
        }

        // Apply delivery window floor in the customer's local timezone
        if (preferences_delivery_window_floor) {
            const floor = preferences_delivery_window_floor;
            const zip = firstOrder?.dropoff_zip;
            const tz = zipcode_to_timezone.lookup(zip) || 'America/New_York';
            const tzOffset = getTimezoneOffset(tz, timeframeStart);

            const localTimeframeStart = new Date(timeframeStart.getTime() + tzOffset);
            const floorLocal = new Date(localTimeframeStart);
            floorLocal.setUTCHours(floor.hours, floor.minutes, 0, 0);
            const floorUTC = new Date(floorLocal.getTime() - tzOffset);

            if (timeframeStart < floorUTC) {
                timeframeStart = floorUTC;
            }
        }

        // Timeframe end is equal to timeframe start + delivery window.
        // For warehouse pickups, this is equal to the service time,
        // For deliveries and customer return pickups it uses the delivery window set on their profile, or defaults to 2 hours if this has not been set.
        let timeframeEnd = add(
            timeframeStart,
            (stop.type === 'PICKUP' && !stop?.returns?.length) || stop.type === 'LUNCH'
                ? serviceTime
                : { hours: preferences_delivery_timeframe, minutes: 0 }
        );

        stop.del_window_start = timeframeStart;
        stop.del_window_end = timeframeEnd;

        stop.driving_time = drivingTime * 1000;
        stop.driving_distance = mileString.format(drivingDistance * 0.000621371);

        // Increment total service time spent at this stop
        totalService.hours += serviceTime.hours;
        totalService.minutes += serviceTime.minutes;
    });

    return {
        stopsWithTimeframes: newStops,
        totalDrivingDistance,
        totalDrivingTime: routeStatDrivingTime,
    };
};
